import React from 'react';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { Link } from 'react-router-dom';
import { WorkspaceApiClient } from '@/infrastructure/workspace/WorkspaceApiClient';
import { getLocaleDateFormatTag } from '@/i18n/localeCatalog';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

function fmtDate(ts, locale) {
  if (!ts) return 'N/A';
  try {
    const tag = getLocaleDateFormatTag(locale);
    return new Date(Number(ts)).toLocaleString(tag);
  } catch {
    return 'N/A';
  }
}

export default function ChildLearningDetailPage({ session, studentId }) {
  const { t, locale } = useAppTranslation();
  const api = React.useMemo(() => new WorkspaceApiClient(), []);
  const token = session?.accessToken || null;
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    if (!token) return undefined;
    setLoading(true);
    api
      .getChildDetail(studentId, token)
      .then((x) => {
        if (!mounted) return;
        setData(x);
      })
      .catch((e) => {
        const raw = String(e?.message || e || '').trim();
        if (/^unauthorized$/i.test(raw) || raw === 'HTTP 401') {
          setError(t('errors.unauthorized'));
        } else {
          setError(formatApiErrorMessage(raw, t) || t('errors.loadChildLearning'));
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [api, studentId, token, t]);

  const student = data?.student;
  const classes = Array.isArray(data?.classes) ? data.classes : [];
  const assignments = Array.isArray(data?.assignments) ? data.assignments : [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-headline text-3xl font-extrabold">{t('childLearning.pageTitle')}</h1>
        <Link
          to="/children"
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#004ac6] hover:text-[#004ac6]"
        >
          <span className="material-symbols-outlined !text-base">arrow_back</span>
          {t('childLearning.backToChildren')}
        </Link>
      </div>
      {loading ? <p className="text-sm text-slate-600">{t('childLearning.loading')}</p> : null}
      {error ? <p className="text-sm font-semibold text-rose-700">{error}</p> : null}

      {student ? (
        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="text-xl font-extrabold text-slate-900">{student.name}</h2>
          <div className="mt-2 grid gap-2 text-sm text-slate-700 md:grid-cols-2">
            <p>
              {t('childLearning.emailId')} {student.email || student.id}
            </p>
            <p>
              {t('childLearning.age')} {student.age || 'N/A'}
            </p>
            <p>
              {t('childLearning.school')} {student.school || 'N/A'}
            </p>
            <p>
              {t('childLearning.phone')} {student.phone || 'N/A'}
            </p>
            <p className="md:col-span-2">
              {t('childLearning.address')} {student.address || 'N/A'}
            </p>
            <p className="md:col-span-2">
              {t('childLearning.parentInfo')} {student.parent_info || 'N/A'}
            </p>
          </div>
        </section>
      ) : null}

      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-bold">{t('childLearning.classesTitle', { count: classes.length })}</h3>
        <div className="mt-3 space-y-2">
          {classes.map((c) => (
            <article key={c.id} className="rounded-xl border border-slate-100 p-3">
              <p className="font-semibold text-slate-900">{c.name}</p>
              <p className="text-xs text-slate-500">
                {t('childLearning.teacher')} {c.teacher_name || 'N/A'}
              </p>
            </article>
          ))}
          {!loading && classes.length === 0 ? <p className="text-sm text-slate-500">{t('childLearning.noClasses')}</p> : null}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-bold">{t('childLearning.assignmentsTitle', { count: assignments.length })}</h3>
        <div className="mt-3 space-y-2">
          {assignments.map((a, idx) => (
            <article key={`${a.class_id}-${a.quiz_id}-${idx}`} className="rounded-xl border border-slate-100 p-3">
              <p className="font-semibold text-slate-900">{a.quiz_title}</p>
              <p className="text-xs text-slate-500">
                {t('childLearning.assignMeta', {
                  className: a.class_name,
                  assignedAt: fmtDate(a.assigned_at, locale),
                  dueAt: fmtDate(a.due_at, locale),
                })}
              </p>
              {a.latest_result_id ? (
                <p className="mt-1 text-sm text-slate-700">
                  {t('childLearning.latestScore', {
                    score: a.latest_score,
                    at: fmtDate(a.latest_timestamp, locale),
                  })}
                </p>
              ) : (
                <p className="mt-1 text-sm font-semibold text-amber-700">{t('childLearning.notSubmitted')}</p>
              )}
            </article>
          ))}
          {!loading && assignments.length === 0 ? (
            <p className="text-sm text-slate-500">{t('childLearning.noAssignments')}</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
